import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  apiGetOpportunityCategories,
  apiGetOpportunities,
  apiCreateOpportunity,
  apiUpdateOpportunity,
  apiDeleteOpportunity,
  apiGetSubcategoryCounts,
  apiExportOpportunities,
} from "@api/opportunity";
import type {
  CreateOpportunityPayload,
  UpdateOpportunityPayload,
  Opportunity,
  OpportunityExportParams,
} from "@api/opportunity";

export function useOpportunityCategories() {
  return useQuery({
    queryKey: ["opportunityCategories"],
    queryFn: apiGetOpportunityCategories,
  });
}

export function useOpportunities(categoryId: string | null, subcategoryId?: string | null) {
  return useQuery({
    queryKey: ["opportunities", categoryId, subcategoryId ?? null],
    queryFn: () => apiGetOpportunities(categoryId!, subcategoryId),
    enabled: !!categoryId,
  });
}

export function useSubcategoryCounts(categoryId: string | null) {
  return useQuery({
    queryKey: ["subcategoryCounts", categoryId],
    queryFn: () => apiGetSubcategoryCounts(categoryId!),
    enabled: !!categoryId,
  });
}

export function useCreateOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateOpportunityPayload) =>
      apiCreateOpportunity(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      queryClient.invalidateQueries({ queryKey: ["subcategoryCounts"] });
    },
  });
}

export function useUpdateOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateOpportunityPayload;
    }) => apiUpdateOpportunity(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      queryClient.invalidateQueries({ queryKey: ["subcategoryCounts"] });
    },
  });
}

export function useExportOpportunities() {
  return useMutation({
    mutationFn: (params: OpportunityExportParams) =>
      apiExportOpportunities(params),
  });
}

export function useDeleteOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: apiDeleteOpportunity,
    onMutate: async (deletedId) => {
      await queryClient.cancelQueries({ queryKey: ["opportunities"] });
      const previousQueries = queryClient.getQueriesData<Opportunity[]>({
        queryKey: ["opportunities"],
      });

      queryClient.setQueriesData<Opportunity[]>(
        { queryKey: ["opportunities"] },
        (old) => old?.filter((o) => o.id !== deletedId),
      );

      return { previousQueries };
    },
    onError: (_err, _vars, context) => {
      context?.previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      queryClient.invalidateQueries({ queryKey: ["subcategoryCounts"] });
    },
  });
}
